/**
 * Form-structure location hints for opening-equity determinations.
 *
 * Highest value-per-line in this wave, and it needs no extraction: once the
 * form type is known, a determination's location is IRS form structure.
 * "Basis is usually on page 2, Schedule B, line 1" still helps an AM holding
 * a scan we cannot read.
 *
 * Wave 1 — tax returns only (1120-S / 1120 / 1065). QB trio has no Citation
 * type yet (owner 2026-08-12).
 */

export const OPENING_FORM_TYPES = ["1120S", "1120", "1065"] as const;
export type OpeningFormType = (typeof OPENING_FORM_TYPES)[number];

/** Display hyphenated 1120-S; store canonical 1120S. */
export function displayFormType(canonical: string): string {
  return canonical === "1120S" ? "1120-S" : canonical;
}

export function canonicalFormType(display: string): string {
  const compact = display.toUpperCase().replace(/[\s-]/g, "");
  if (compact === "1120S") return "1120S";
  if (compact === "1120") return "1120";
  if (compact === "1065") return "1065";
  return display;
}

export const DETERMINATION_LOCATION_FIELDS = [
  "form",
  "tax_year",
  "basis",
] as const;
export type DeterminationLocationField =
  (typeof DETERMINATION_LOCATION_FIELDS)[number];

export interface DeterminationLocationHint {
  readonly field: DeterminationLocationField;
  readonly form: OpeningFormType;
  /** AM-facing sentence. No document text — form structure only. */
  readonly summary: string;
}

/**
 * IRS 2025 form structure (not extracted spans). Hyphenated 1120-S is the
 * official title; stored form type is 1120S (extractor closed set).
 */
export const DETERMINATION_LOCATION_HINTS: readonly DeterminationLocationHint[] =
  [
    {
      field: "form",
      form: "1120S",
      summary: "Form type is usually on page 1, in the title: Form 1120-S",
    },
    {
      field: "tax_year",
      form: "1120S",
      summary: "Tax year is usually on page 1, in the return header",
    },
    {
      field: "basis",
      form: "1120S",
      summary:
        "Basis is usually on page 2, Schedule B, line 1 (accounting method)",
    },
    {
      field: "form",
      form: "1120",
      summary: "Form type is usually on page 1, in the title: Form 1120",
    },
    {
      field: "tax_year",
      form: "1120",
      summary: "Tax year is usually on page 1, in the return header",
    },
    {
      field: "basis",
      form: "1120",
      summary:
        "Basis is usually on page 1, Accounting method (Cash / Accrual / Other)",
    },
    {
      field: "form",
      form: "1065",
      summary: "Form type is usually on page 1, in the title: Form 1065",
    },
    {
      field: "tax_year",
      form: "1065",
      summary: "Tax year is usually on page 1, in the return header",
    },
    {
      field: "basis",
      form: "1065",
      summary:
        "Basis is usually on page 1, Accounting method (Cash / Accrual / Other)",
    },
  ];

/** Map IRS / OCR spellings onto the closed form set. Unknown → null. */
export function normalizeOpeningFormType(
  raw: string | null | undefined,
): OpeningFormType | null {
  if (raw === undefined || raw === null) return null;
  const compact = raw.replace(/[\s-]/g, "").toUpperCase();
  if (compact === "1120S") return "1120S";
  if (compact === "1120") return "1120";
  if (compact === "1065") return "1065";
  return null;
}

export function isDeterminationLocationField(
  field: string,
): field is DeterminationLocationField {
  return (DETERMINATION_LOCATION_FIELDS as readonly string[]).includes(field);
}

/**
 * Copy telling the AM where to look. Form-specific when known; otherwise
 * every usual location for that field (the unreadable-document case).
 */
export function locationHintFor(
  field: string,
  form?: string | null,
): string | null {
  if (!isDeterminationLocationField(field)) return null;
  const known = normalizeOpeningFormType(form);
  const matches = DETERMINATION_LOCATION_HINTS.filter((h) => h.field === field);
  if (matches.length === 0) return null;
  if (known !== null) {
    const hit = matches.find((h) => h.form === known);
    return hit?.summary ?? null;
  }
  return matches.map((h) => h.summary).join(" ");
}
